import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import logoOrig from '../img/tmis_logo.png';
import logoWhite from '../img/tmis_logo_w.png';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../context/PermissionsContext';
import { usePopupCard, PopupCard, PopupCardLink, PopupCardButton } from './FlyoutCard';

// ── Navigation structure ──────────────────────────────────────────────────────
const NAV_PANELS = {
  dash: {
    label: 'Home',
    icon:  'bi-speedometer2',
    sections: [
      { label: 'Overview', items: [
        { label: 'Dashboard', icon: 'bi-speedometer2', to: '/' },
        { label: 'Insights',  icon: 'bi-graph-up',     to: '/insights' },
      ]},
    ],
  },
  rec: {
    label: 'Records',
    icon:  'bi-folder2-open',
    badgeKey: 'openRequests',
    badgeTone: 'blue',
    badgeCountLabel: { singular: 'open request', plural: 'open requests' },
    sections: [
      { label: 'Master', items: [
        { label: 'Course Level Master', icon: 'bi-bar-chart-steps',  to: '/records/course-levels', module: 'course_level_master' },
        { label: 'Course Master',       icon: 'bi-journal-bookmark', to: '/records/courses',       module: 'course_master' },
        { label: 'Intake Master',       icon: 'bi-table',            to: '/records/intakes',       module: 'intake_master' },
        { label: 'Component Master',    icon: 'bi-diagram-3',        to: '/records/components',    module: 'component_master' },
        { label: 'Module Master',       icon: 'bi-collection',       to: '/records/modules',       module: 'module_master' },
        { label: 'Marks Review',        icon: 'bi-clipboard2-check', to: '/records/marks-review',  module: 'marks_review' },
        { label: 'Marking Permissions', icon: 'bi-person-check',     to: '/records/marking-permissions', module: 'trainer_marking_permissions' },
        { label: 'eSurvey Links',       icon: 'bi-link-45deg',       to: '/records/esurvey-links', module: 'survey_links' },
      ]},
      { label: 'Case records', items: [
        { label: 'Disciplinary', icon: 'bi-exclamation-circle', to: '/records/disciplinary', module: 'disciplinary' },
        { label: 'Requests',     icon: 'bi-envelope-open',      to: '/records/requests',     module: 'requests', badgeKey: 'openRequests', badgeTone: 'blue', badgeCountLabel: { singular: 'open request', plural: 'open requests' } },
      ]},
    ],
  },
  stu: {
    label: 'Students',
    icon:  'bi-people-fill',
    sections: [
      { label: 'Profiles', items: [
        { label: 'Student Profile',      icon: 'bi-person-badge',  to: '/students/profile',            module: 'students' },
      ]},
      { label: 'Academic', items: [
        { label: 'Attendance',           icon: 'bi-calendar-check', to: '/students/attendance', module: 'attendance' },
        { label: 'Academic Performance', icon: 'bi-graph-up',       to: '/students/academic',   module: 'academic_performance' },
        { label: 'Activities',           icon: 'bi-activity',       to: '/students/activities', module: 'activities' },
      ]},
      { label: 'Records', items: [
        { label: 'Alumni',               icon: 'bi-award',           to: '/records/alumni',       module: 'alumni' },
      ]},
    ],
  },
  trn: {
    label: 'Trainers',
    icon:  'bi-person-badge',
    badgeKeys: ['claimsQueue', 'assessmentsQueue', 'observationLogs', 'taeppRequests'],
    badgeTone: 'blue',
    badgeCountLabel: { singular: 'trainer item', plural: 'trainer items' },
    sections: [
      { label: 'Directory', items: [
        { label: 'Weekly Schedule',  icon: 'bi-calendar3',        to: '/trainers/schedule',     module: 'trainer_schedule' },
        { label: 'Trainer Profiles', icon: 'bi-people',           to: '/trainers/directory',    module: 'trainer_directory' },
        { label: 'Fee Rates',        icon: 'bi-currency-dollar',  to: '/trainers/fee-rates',    module: 'trainer_fee_rates' },
      ]},
      { label: 'Requests', items: [
        { label: 'Claims Queue',     icon: 'bi-receipt',          to: '/trainers/claims',       module: 'trainer_claims_queue', badgeKey: 'claimsQueue',      badgeTone: 'amber',  badgeCountLabel: { singular: 'claim needing action', plural: 'claims needing action' } },
        { label: 'Assessments',      icon: 'bi-journal-check',    to: '/trainers/assessments',  module: 'trainer_assessments',  badgeKey: 'assessmentsQueue', badgeTone: 'violet', badgeCountLabel: { singular: 'assessment awaiting review', plural: 'assessments awaiting review' } },
        { label: 'Observation Logs', icon: 'bi-clipboard-check',  to: '/trainers/observations', module: 'trainer_observations', badgeKey: 'observationLogs',  badgeTone: 'teal',   badgeCountLabel: { singular: 'observation log', plural: 'observation logs' } },
        { label: 'TAEPP Requests',   icon: 'bi-file-earmark-check', to: '/trainers/taepp',      approverOnly: true,   badgeKey: 'taeppRequests',    badgeTone: 'amber',  badgeCountLabel: { singular: 'TAEPP item needing action', plural: 'TAEPP items needing action' } },
      ]},
    ],
  },
  pay: {
    label: 'Payment',
    icon:  'bi-cash-coin',
    sections: [
      { label: 'Payments', items: [
        { label: 'Receipt', icon: 'bi-receipt', to: '/payment/receipts', module: 'receipts' },
      ]},
    ],
  },
  rpt: {
    label: 'Reports',
    icon:  'bi-file-earmark-bar-graph',
    sections: [
      { label: 'Students', items: [
        { label: 'Progression Report', icon: 'bi-graph-up-arrow', to: '/reports/progression', module: 'reports' },
        { label: 'Class Passing Rate', icon: 'bi-bar-chart-line', to: '/reports/passing-rate', module: 'reports' },
      ]},
    ],
  },
  sys: {
    label: 'System',
    icon:  'bi-gear-fill',
    sections: [
      { label: 'Admin', items: [
        { label: 'Audit Logs',   icon: 'bi-shield-check',          to: '/system/audit-logs',   module: 'audit_logs', adminOnly: false },
        { label: 'Users',        icon: 'bi-person-gear',            to: '/system/users',        superAdminOnly: true },
        { label: 'Permissions',  icon: 'bi-toggles',                to: '/system/permissions',  superAdminOnly: true },
        { label: 'Approval Settings', icon: 'bi-person-check',      to: '/system/approval-settings', deptOnly: 'DEP-09' },
      ]},
    ],
  },
};

// Build a route → breadcrumb map
const BREADCRUMBS = {};
Object.entries(NAV_PANELS).forEach(([panelId, panel]) => {
  panel.sections.forEach(sec => {
    sec.items.forEach(item => {
      BREADCRUMBS[item.to] = {
        trail: [panel.label, sec.label, item.label],
        panelId,
      };
    });
  });
});

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
}

export default function Layout({ children }) {
  const { user, avatarColor, logout } = useAuth();
  const { theme, toggle }   = useTheme();
  const { canView }         = usePermissions();
  const navigate            = useNavigate();
  const location            = useLocation();

  const [mobileOpen,   setMobileOpen]   = useState(false);
  // Mobile drawer: only one section's submenu expanded at a time
  const [mobileExpanded, setMobileExpanded] = useState(null);
  // Desktop sidebar: only one panel expanded at a time. Nothing auto-expands to
  // match the current route; a panel only opens or closes on an explicit click.
  // Every route renders its own <Layout>, so this state is remounted on each
  // navigation — the open panel is kept in sessionStorage so picking a submenu
  // item doesn't close it.
  const [deskExpanded, setDeskExpanded] = useState(() => {
    try {
      const saved = sessionStorage.getItem('sms_sidebar_expanded_panel');
      return saved && NAV_PANELS[saved] ? saved : null;
    } catch { return null; }
  });
  useEffect(() => {
    try {
      if (deskExpanded) sessionStorage.setItem('sms_sidebar_expanded_panel', deskExpanded);
      else sessionStorage.removeItem('sms_sidebar_expanded_panel');
    } catch { /* storage unavailable — the panel just won't persist */ }
  }, [deskExpanded]);
  // Single sidebar collapse (icons-only) state — persisted per browser,
  // replacing the old rail + flyout-panel pair with one column that just
  // shrinks in place.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sms_sidebar_collapsed') === '1');
  const [navCounts, setNavCounts] = useState({});
  const navReadPendingRef = useRef(new Set());
  const navCountRequestVersionRef = useRef(0);
  const loadNavCountsRef = useRef(null);

  // Sidebar container refs — the flyout/account popup cards anchor off of
  // whichever one is actually visible (desktop rail vs. off-canvas drawer).
  const sidebarRef = useRef(null);
  const mobileDrawerRef = useRef(null);
  const getSidebarRect = useCallback(() => {
    const el = window.innerWidth < 768 ? mobileDrawerRef.current : sidebarRef.current;
    return el ? el.getBoundingClientRect() : null;
  }, []);

  const navFlyout = usePopupCard({ getSidebarRect, mode: 'flyout' });
  const acctMenu   = usePopupCard({ getSidebarRect, mode: 'account' });

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const toggleCollapsed = () => {
    // Expanding back out never auto-reveals whatever was open before
    // collapsing — the submenu only opens again on an explicit click.
    setDeskExpanded(null);
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sms_sidebar_collapsed', next ? '1' : '0');
      return next;
    });
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const closeMobile = () => { setMobileOpen(false); setMobileExpanded(null); };

  // Breadcrumb for current route
  const bc = BREADCRUMBS[location.pathname] || { trail: ['Dashboard'], panelId: 'dash' };

  const isDark = theme === 'dark';

  // Keep sidebar badges lightweight and permission-protected. Focus and
  // visibility refreshes make new activity appear promptly; the interval
  // covers an admin who leaves the app open on another page.
  useEffect(() => {
    navReadPendingRef.current.clear();
    navCountRequestVersionRef.current += 1;
    if (!user) {
      setNavCounts({});
      return undefined;
    }

    let cancelled = false;
    const loadNavCounts = () => {
      const requestVersion = navCountRequestVersionRef.current;
      api.get('/navigation/counts')
        .then(res => {
          if (!cancelled && requestVersion === navCountRequestVersionRef.current) {
            const counts = res.data?.data || {};
            navReadPendingRef.current.forEach(key => {
              if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] = 0;
            });
            setNavCounts(Object.fromEntries(Object.entries(counts).map(([key, value]) => [key, Number(value) || 0])));
          }
        })
        .catch(() => {});
    };
    loadNavCountsRef.current = loadNavCounts;
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') loadNavCounts();
    };

    loadNavCounts();
    const interval = window.setInterval(loadNavCounts, 30000);
    window.addEventListener('focus', loadNavCounts);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', loadNavCounts);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      if (loadNavCountsRef.current === loadNavCounts) loadNavCountsRef.current = null;
    };
  }, [user, canView]);

  // A queue is acknowledged as soon as its submenu/page is opened. Keep the
  // local badge hidden while the request is in flight, then refresh so a
  // genuinely newer record can appear again immediately after it arrives.
  const markNavRead = useCallback(key => {
    if (!key) return;
    navReadPendingRef.current.add(key);
    navCountRequestVersionRef.current += 1;
    setNavCounts(prev => ({ ...prev, [key]: 0 }));

    api.post('/navigation/notifications/read', { key })
      .then(() => {
        navReadPendingRef.current.delete(key);
        navCountRequestVersionRef.current += 1;
        loadNavCountsRef.current?.();
      })
      .catch(() => {
        navReadPendingRef.current.delete(key);
        navCountRequestVersionRef.current += 1;
      });
  }, []);

  // Direct links, browser refreshes, and opening a page from outside the
  // sidebar should acknowledge the same queue as an explicit menu click.
  useEffect(() => {
    const item = Object.values(NAV_PANELS)
      .flatMap(panel => panel.sections.flatMap(section => section.items))
      .find(navItem => navItem.to === location.pathname && navItem.badgeKey);
    if (item?.badgeKey) markNavRead(item.badgeKey);
  }, [location.pathname, markNavRead]);

  const getNavCount = item => {
    const count = item.badgeKey ? navCounts[item.badgeKey] : 0;
    return Number.isFinite(count) && count > 0 ? count : 0;
  };
  const getPanelCount = panel => {
    if (Array.isArray(panel.badgeKeys)) return panel.badgeKeys.reduce((sum, key) => sum + (Number(navCounts[key]) || 0), 0);
    return getNavCount(panel);
  };
  const badgeClass = tone => tone ? `sms-nav-count-${tone}` : '';
  const badgeAnnouncement = (entry, count) => {
    const labels = entry.badgeCountLabel || { singular: 'notification', plural: 'notifications' };
    return `${entry.label}, ${count} ${count === 1 ? labels.singular : labels.plural}`;
  };

  return (
    <div className="sms-app">
      <a href="#main-content" className="skip-link">Skip to main content</a>

      <div className="sms-layout-shell">
        {/* ── Sidebar — one column, collapses to icons-only in place ── */}
        <aside ref={sidebarRef} className={`sms-sidebar ${collapsed ? 'collapsed' : ''}`} aria-label="Main navigation">
          {/* Collapsed: the whole header row becomes the expand control (the
              toggle button itself is hidden then — no room, and no need for
              two overlapping click targets). */}
          <div
            className="sms-sidebar-head"
            onClick={collapsed ? toggleCollapsed : undefined}
            role={collapsed ? 'button' : undefined}
            tabIndex={collapsed ? 0 : undefined}
            onKeyDown={collapsed ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapsed(); } }) : undefined}
            aria-label={collapsed ? 'Expand sidebar' : undefined}
          >
            {collapsed ? (
              <img src="/favicon.svg" alt="TMIS SMS" className="sms-sidebar-badge-icon" />
            ) : (
              <img src={isDark ? logoWhite : logoOrig} alt="TMIS SMS" className="sms-sidebar-title-logo" />
            )}
            <button
              type="button"
              className="sms-sidebar-collapse-btn"
              onClick={e => { e.stopPropagation(); toggleCollapsed(); }}
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <i className="bi bi-chevron-bar-left" aria-hidden="true" />
            </button>
          </div>

          <nav className="sms-panel-body" aria-label="Primary navigation">
            {/* One row markup for every state — collapsing/expanding never
                swaps in a different element, so the icon never jumps or
                changes size. CSS alone hides the label/chevron when
                collapsed; only the click/hover behavior branches (open the
                flyout vs. toggle the inline accordion). */}
            {Object.entries(NAV_PANELS).map(([panelId, panel]) => {
              const expanded = !collapsed && deskExpanded === panelId;
              const hasActive = bc.panelId === panelId;
              const panelCount = getPanelCount(panel);
              return (
                <div key={panelId} className="sms-panel-group">
                  <button
                    type="button"
                    className={`sms-panel-header ${hasActive ? 'has-active' : ''}`}
                    onClick={e => {
                      if (collapsed) navFlyout.toggle(panelId, e.currentTarget);
                      else setDeskExpanded(prev => prev === panelId ? null : panelId);
                    }}
                    onMouseEnter={collapsed ? (e => navFlyout.open(panelId, e.currentTarget)) : undefined}
                    onMouseLeave={collapsed ? navFlyout.scheduleClose : undefined}
                    onFocus={collapsed ? (e => navFlyout.open(panelId, e.currentTarget)) : undefined}
                    aria-label={panelCount ? badgeAnnouncement(panel, panelCount) : (collapsed ? panel.label : undefined)}
                    aria-haspopup={collapsed ? 'menu' : undefined}
                    aria-expanded={collapsed ? navFlyout.openId === panelId : expanded}
                  >
                    <span className="sms-nav-icon-col"><i className={`bi ${panel.icon}`} aria-hidden="true" /></span>
                    <span className="sms-panel-header-label">{panel.label}</span>
                    {panelCount > 0 && <span className={`sms-nav-count sms-panel-count ${badgeClass(panel.badgeTone)}`} aria-hidden="true">{panelCount > 99 ? '99+' : panelCount}</span>}
                    <i className={`bi bi-chevron-down sms-panel-header-chevron ${expanded ? 'expanded' : ''}`} aria-hidden="true" />
                  </button>
                  {/* Always rendered (not `expanded &&`) so the CSS height transition
                      slides it down on open and back up on close. */}
                  {(
                    <div className={`sms-panel-acc ${expanded ? 'expanded' : ''}`}>
                      <div className="sms-panel-acc-inner sms-panel-sub">
                        {panel.sections.flatMap(sec => sec.items).map(item => {
                          if (item.superAdminOnly && user?.role !== 'super_admin') return null;
                          if (item.deptOnly && user?.dept_code !== item.deptOnly) return null;
                          if (item.approverOnly && navCounts.taeppRequests === undefined) return null;
                          const count = getNavCount(item);

                          return (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              data-label={item.label}
                              className={({ isActive }) =>
                                `sms-panel-item ${isActive ? 'active' : ''}`
                              }
                              aria-label={count ? badgeAnnouncement(item, count) : undefined}
                              aria-current={location.pathname === item.to ? 'page' : undefined}
                              onClick={item.badgeKey ? () => markNavRead(item.badgeKey) : undefined}
                            >
                              <span className="sms-nav-icon-col"><i className={`bi ${item.icon}`} aria-hidden="true" /></span>
                              <span className="sms-panel-item-label">{item.label}</span>
                              {count > 0 && <span className={`sms-nav-count ${badgeClass(item.badgeTone)}`} aria-hidden="true">{count > 99 ? '99+' : count}</span>}
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer — theme segmented toggle, then the signed-in user card
              which opens the account popup card (log out) on click. */}
          <div className="sms-panel-foot-wrap">
            <div className="sms-sidebar-theme-toggle" role="group" aria-label="Theme">
              <button type="button" className={`sms-sidebar-theme-seg ${!isDark ? 'active' : ''}`}
                onClick={() => isDark && toggle()} aria-pressed={!isDark}>
                <i className="bi bi-sun-fill" aria-hidden="true" />
                <span className="sms-sidebar-theme-seg-label">Light</span>
              </button>
              <button type="button" className={`sms-sidebar-theme-seg ${isDark ? 'active' : ''}`}
                onClick={() => !isDark && toggle()} aria-pressed={isDark}>
                <i className="bi bi-moon-stars-fill" aria-hidden="true" />
                <span className="sms-sidebar-theme-seg-label">Dark</span>
              </button>
            </div>
            {/* Collapsed-only stand-in for the segmented control above — a
                single circular icon button that always flips the theme
                (the segmented pair's individual segments are hidden then,
                so this is the only way to reach dark/light while collapsed). */}
            <button type="button" className="sms-sidebar-theme-collapsed-btn" onClick={toggle}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}>
              <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="sms-panel-foot-trigger"
              onClick={e => acctMenu.toggle('account', e.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={acctMenu.openId === 'account'}
              aria-label={`Account menu for ${user?.full_name || 'account'}`}
            >
              <div className="sms-panel-foot-avatar" style={{ background: avatarColor || undefined }}>
                {initials(user?.full_name)}
              </div>
              <div className="sms-panel-foot-user">
                <div className="sms-panel-foot-name">{user?.full_name}</div>
                <div className="sms-panel-foot-email">{user?.email}</div>
              </div>
              <i className="bi bi-three-dots-vertical sms-panel-foot-more" aria-hidden="true" />
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <div className="sms-main-wrap">
          {/* Topbar with breadcrumb */}
          <header className="sms-topbar" role="banner">
            <div className="sms-topbar-left">
              <button className="sms-hamburger" onClick={() => setMobileOpen(true)} aria-label="Open navigation menu">
                <i className="bi bi-list" aria-hidden="true" />
              </button>
              <nav className="sms-breadcrumb" aria-label="Breadcrumb">
              <ol className="sms-breadcrumb-list">
                {bc.trail.map((crumb, i) => (
                  <li key={i} className="sms-breadcrumb-item">
                    {i > 0 && <span className="sms-breadcrumb-sep" aria-hidden="true">›</span>}
                    {i === bc.trail.length - 1
                      ? <span className="sms-breadcrumb-current" aria-current="page">{crumb}</span>
                      : <span className="sms-breadcrumb-ancestor">{crumb}</span>
                    }
                  </li>
                ))}
              </ol>
            </nav>
            </div>
          </header>

          {/* Page */}
          <main id="main-content" className="sms-page-wrap" tabIndex="-1" key={location.pathname}>
            {/* The TAEPP approver is told on every page while verified claims wait for their signature. */}
            {navCounts.claimsApproval > 0 && location.pathname !== '/trainers/claims' && (
              <div className="sms-approval-banner" role="alert">
                <i className="bi bi-patch-exclamation" aria-hidden="true" />
                <div className="sms-approval-banner-text">
                  <strong>{navCounts.claimsApproval} claim{navCounts.claimsApproval === 1 ? ' is' : 's are'} waiting for your signature</strong>
                  <span>They have been verified. Review each one and sign to approve it, or reject it.</span>
                </div>
                <button type="button" className="sms-btn sms-btn-primary" onClick={() => navigate('/trainers/claims', { state: { filter: 'verified' } })}>
                  <i className="bi bi-pen" aria-hidden="true" /> Review and Sign
                </button>
              </div>
            )}
            {children}
          </main>
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      {mobileOpen && (
        <div className="sms-mobile-backdrop" onClick={closeMobile} aria-hidden="true" />
      )}
      <div ref={mobileDrawerRef} className={`sms-mobile-drawer ${mobileOpen ? 'open' : ''}`} aria-label="Mobile navigation" aria-hidden={!mobileOpen}>
        {/* Drawer header */}
        <div className="sms-mobile-drawer-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={isDark ? logoWhite : logoOrig} alt="TMIS SMS" style={{ height: 32, width: 'auto' }} />
          </div>
          <button className="sms-mobile-drawer-close" onClick={closeMobile} aria-label="Close navigation">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        {/* Scrollable nav body — same accordion structure as the desktop
            sidebar, driven by mobileExpanded instead of deskExpanded */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '6px 0' }}>
          {Object.entries(NAV_PANELS).map(([panelId, panel]) => {
            const expanded = mobileExpanded === panelId;
            const hasActive = bc.panelId === panelId;
            const panelCount = getPanelCount(panel);
            return (
              <div key={panelId} className="sms-panel-group">
                <button
                  type="button"
                  className={`sms-panel-header ${hasActive ? 'has-active' : ''}`}
                  onClick={() => setMobileExpanded(prev => prev === panelId ? null : panelId)}
                  aria-label={panelCount ? badgeAnnouncement(panel, panelCount) : undefined}
                  aria-expanded={expanded}
                >
                  <span className="sms-nav-icon-col"><i className={`bi ${panel.icon}`} aria-hidden="true" /></span>
                  <span className="sms-panel-header-label">{panel.label}</span>
                  {panelCount > 0 && <span className={`sms-nav-count sms-panel-count ${badgeClass(panel.badgeTone)}`} aria-hidden="true">{panelCount > 99 ? '99+' : panelCount}</span>}
                  <i className={`bi bi-chevron-down sms-panel-header-chevron ${expanded ? 'expanded' : ''}`} aria-hidden="true" />
                </button>
                <div className={`sms-panel-acc ${expanded ? 'expanded' : ''}`}>
                  <div className="sms-panel-acc-inner sms-panel-sub">
                    {panel.sections.flatMap(sec => sec.items).map(item => {
                      if (item.superAdminOnly && user?.role !== 'super_admin') return null;
                      if (item.deptOnly && user?.dept_code !== item.deptOnly) return null;
                      if (item.approverOnly && navCounts.taeppRequests === undefined) return null;
                      const count = getNavCount(item);
                      return (
                        <NavLink key={item.to} to={item.to}
                          className={({ isActive }) => `sms-panel-item ${isActive ? 'active' : ''}`}
                          aria-label={count ? badgeAnnouncement(item, count) : undefined}
                          aria-current={location.pathname === item.to ? 'page' : undefined}
                          onClick={() => { if (item.badgeKey) markNavRead(item.badgeKey); closeMobile(); }}>
                          <span className="sms-nav-icon-col"><i className={`bi ${item.icon}`} aria-hidden="true" /></span>
                          <span className="sms-panel-item-label">{item.label}</span>
                          {count > 0 && <span className={`sms-nav-count ${badgeClass(item.badgeTone)}`} aria-hidden="true">{count > 99 ? '99+' : count}</span>}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer — same theme toggle + account-popup trigger as the desktop
            sidebar. The drawer never carries a "collapsed" class, so the
            segmented control always renders in its full expanded form here
            regardless of the desktop rail's collapse state. */}
        <div className="sms-mobile-drawer-foot">
          <div className="sms-sidebar-theme-toggle" role="group" aria-label="Theme">
            <button type="button" className={`sms-sidebar-theme-seg ${!isDark ? 'active' : ''}`}
              onClick={() => isDark && toggle()} aria-pressed={!isDark}>
              <i className="bi bi-sun-fill" aria-hidden="true" />
              <span className="sms-sidebar-theme-seg-label">Light</span>
            </button>
            <button type="button" className={`sms-sidebar-theme-seg ${isDark ? 'active' : ''}`}
              onClick={() => !isDark && toggle()} aria-pressed={isDark}>
              <i className="bi bi-moon-stars-fill" aria-hidden="true" />
              <span className="sms-sidebar-theme-seg-label">Dark</span>
            </button>
          </div>
          <button
            type="button"
            className="sms-panel-foot-trigger"
            onClick={e => acctMenu.toggle('account', e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={acctMenu.openId === 'account'}
            aria-label={`Account menu for ${user?.full_name || 'account'}`}
          >
            <div className="sms-panel-foot-avatar" style={{ background: avatarColor || undefined }}>
              {initials(user?.full_name)}
            </div>
            <div className="sms-panel-foot-user">
              <div className="sms-panel-foot-name">{user?.full_name}</div>
              <div className="sms-panel-foot-email">{user?.email}</div>
            </div>
            <i className="bi bi-three-dots-vertical sms-panel-foot-more" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* ── Nav-section flyout (collapsed rail) ── */}
      {navFlyout.openId && NAV_PANELS[navFlyout.openId] && (
        <PopupCard
          pos={navFlyout.pos}
          cardRef={navFlyout.cardRef}
          label={NAV_PANELS[navFlyout.openId].label}
          onMouseEnter={navFlyout.cancelClose}
          onMouseLeave={navFlyout.scheduleClose}
        >
          {NAV_PANELS[navFlyout.openId].sections.flatMap(sec => sec.items).map(item => {
            if (item.superAdminOnly && user?.role !== 'super_admin') return null;
            if (item.deptOnly && user?.dept_code !== item.deptOnly) return null;
            if (item.approverOnly && navCounts.taeppRequests === undefined) return null;
            const count = getNavCount(item);
            return (
              <PopupCardLink
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                active={location.pathname === item.to}
                badge={count > 0 ? (count > 99 ? '99+' : count) : null}
                badgeTone={item.badgeTone}
                badgeLabel={count ? badgeAnnouncement(item, count) : undefined}
                onClick={() => { if (item.badgeKey) markNavRead(item.badgeKey); navFlyout.closeNow(); }}
              />
            );
          })}
        </PopupCard>
      )}

      {/* ── Account menu ── */}
      {acctMenu.openId === 'account' && (
        <PopupCard
          pos={acctMenu.pos}
          cardRef={acctMenu.cardRef}
          label={user?.full_name}
          onMouseEnter={acctMenu.cancelClose}
          onMouseLeave={acctMenu.scheduleClose}
        >
          <PopupCardLink
            to="/system/settings"
            icon="bi-sliders"
            label="Settings"
            active={location.pathname === '/system/settings'}
            onClick={acctMenu.closeNow}
          />
          <PopupCardButton
            icon="bi-box-arrow-right"
            label="Log out"
            danger
            onClick={() => { acctMenu.closeNow(); handleLogout(); }}
          />
        </PopupCard>
      )}
    </div>
  );
}
